import { memo, useCallback, useEffect } from 'react';
import { logger } from '@zep/core-logger';
import { useBuildInfoContext } from '../context/BuildInfoProvider';
import { useShowQuizSetupModal } from '../features/quiz-setup-modal';
import { useConfirmPersistData, useSetPersistButtonVisible } from '../hook/useConfirmPersistData';
import { useOpenContinueBuildConfirmModal } from '../modal/ContinueBuildConfirmModal';

/**
 * Build 페이지 초기 마운트 시 실행되는 컴포넌트.
 *
 * memo + useEffect([])로 마운트 1회만 실행됩니다.
 * onMount가 안정적인 참조이면 memo가 re-render를 완전히 차단합니다.
 */
const NormalMountEffect = memo(function NormalMountEffect({
  onMount
}: {
  onMount: () => Promise<void>;
}) {
  useEffect(() => {
    void onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
});

/**
 * Build 페이지 초기화 통합 컴포넌트
 *
 * FormProvider 안에, SafeEnterTracking의 형제 노드로 배치합니다.
 * SafeEnterTracking의 isMounted 전환으로 인한 unmount/remount의 영향을 받지 않습니다.
 *
 * 마운트 시 일반 mount effect 실행 (Quiz Setup Modal 등).
 * 구 랜딩 auto-trigger(AI 모달 자동 오픈) 경로는 대화형 AI 빌더 전환으로 제거되었습니다.
 */
export const BuildPageInitializer = () => {
  const {
    action
  } = useBuildInfoContext();
  const {
    open: openQuizSetupModal
  } = useShowQuizSetupModal();
  const {
    persistData,
    handleConfirmPersistData
  } = useConfirmPersistData();
  const setIsPersistButtonVisible = useSetPersistButtonVisible();
  const {
    open: openContinueBuildConfirmModal,
    isOpened: isOpenedContinueBuildConfirmModal
  } = useOpenContinueBuildConfirmModal();

  // useCallback(fn, [])으로 마운트 시점의 값을 캡처 (의도적 stale closure)
  // NormalMountEffect는 마운트 1회만 실행하므로 이 값들이 정확합니다.
  const handleMount = useCallback(async () => {
    if (action !== 'CREATE') return;
    if (persistData.data) {
      if (isOpenedContinueBuildConfirmModal.current) return;
      const confirmed = await openContinueBuildConfirmModal();
      if (confirmed) {
        handleConfirmPersistData(persistData);
        setIsPersistButtonVisible(false);
        logger.track('save_quiz_data', {
          btn: 'continue'
        });
        return;
      }
      const setupCompleted = await openQuizSetupModal();
      setIsPersistButtonVisible(!setupCompleted);
      return;
    }
    await openQuizSetupModal();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <NormalMountEffect onMount={handleMount} data-sentry-element="NormalMountEffect" data-sentry-component="BuildPageInitializer" data-sentry-source-file="BuildPageInitializer.tsx" />;
};